// src/pages/AdminPage/modals/EditUserModal.tsx
import { useState, useEffect } from "react";
import { X } from "lucide-react";
import type { UserItem } from "../utils/adminTypes";

interface EditUserModalProps {
  user: UserItem | null;
  onClose: () => void;
  onSave: (id: string, data: Partial<UserItem>) => Promise<void>;
}

export default function EditUserModal({
  user,
  onClose,
  onSave,
}: EditUserModalProps) {
  const [form, setForm] = useState<UserItem | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(user ? { ...user } : null);
  }, [user]);

  if (!form) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form.id, {
        name: form.name,
        role: form.role,
        is_active: form.is_active,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
      }}
      onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#111827",
          border: "1px solid #1E2D45",
          borderRadius: 20,
          padding: 24,
          width: "100%",
          maxWidth: 400,
        }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}>
          <h3
            style={{ color: "#fff", fontWeight: 800, fontSize: 16, margin: 0 }}>
            Foydalanuvchini tahrirlash
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#64748B",
              cursor: "pointer",
            }}>
            <X size={18} />
          </button>
        </div>

        {/* Ism */}
        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              display: "block",
              color: "#64748B",
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 6,
            }}>
            Ism
          </label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            style={{
              width: "100%",
              padding: "10px 14px",
              background: "#0D1526",
              border: "1px solid #1E2D45",
              borderRadius: 10,
              color: "#E2E8F0",
              fontSize: 13,
            }}
          />
        </div>

        {/* Email (readonly) */}
        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              display: "block",
              color: "#64748B",
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 6,
            }}>
            Email
          </label>
          <input
            value={form.email || ""}
            readOnly
            style={{
              width: "100%",
              padding: "10px 14px",
              background: "#0A0E1A",
              border: "1px solid #1E2D45",
              borderRadius: 10,
              color: "#64748B",
              fontSize: 13,
              cursor: "not-allowed",
            }}
          />
        </div>

        {/* Role */}
        <div style={{ marginBottom: 14 }}>
          <label
            style={{
              display: "block",
              color: "#64748B",
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 6,
            }}>
            Rol
          </label>
          <select
            value={form.role}
            onChange={(e) =>
              setForm({ ...form, role: e.target.value as "USER" | "ADMIN" })
            }
            style={{
              width: "100%",
              padding: "10px 14px",
              background: "#0D1526",
              border: "1px solid #1E2D45",
              borderRadius: 10,
              color: "#E2E8F0",
              fontSize: 13,
            }}>
            <option value="USER">User</option>
            <option value="ADMIN">Admin</option>
          </select>
        </div>

        {/* Active */}
        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
            }}>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked })
              }
            />
            <span style={{ color: "#E2E8F0", fontSize: 13 }}>Faol</span>
          </label>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 12,
              border: "1px solid #1E2D45",
              background: "transparent",
              color: "#E2E8F0",
              fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.5 : 1,
            }}>
            Bekor
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1,
              padding: "11px",
              borderRadius: 12,
              border: "none",
              background: "#3B82F6",
              color: "#fff",
              fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}>
            {saving ? "Saqlanmoqda..." : "Saqlash"}
          </button>
        </div>
      </div>
    </div>
  );
}
